import React, { useCallback, useMemo, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import KendoActionsCell from '@components/Kendo/ActionsCell';
import { Dialog } from '@progress/kendo-react-dialogs';
import I18n, { translate } from '@utils/i18n';
import api from '@utils/axios';
import toast from 'react-hot-toast';
import { invoicePath, markAsVoidedInvoicePath } from 'routes';
import { Button } from '@ui';
import { SendInvoiceModal } from '@features/Invoices/SendInvoiceModal';
import ColumnMenuContext from './ColumnMenuContext';
import RecordPaymentModal from './RecordPaymentModal';

const namespace = 'features.invoices.index.table.actions';

function ActionsCell({ dataItem, colSpan, className, ariaColumnIndex, style }) {
  const { onDataChanged } = useContext(ColumnMenuContext);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [voidDialogVisible, setVoidDialogVisible] = useState(false);
  const [sendEmailModalVisible, setSendEmailModalVisible] = useState(false);
  const [recordPaymentModalVisible, setRecordPaymentModalVisible] = useState(false);

  const toggleDeleteDialog = useCallback(() => setDeleteDialogVisible((prev) => !prev), []);
  const toggleVoidDialog = useCallback(() => setVoidDialogVisible((prev) => !prev), []);
  const toggleSendEmailModal = useCallback(() => setSendEmailModalVisible((prev) => !prev), []);
  const toggleRecordPaymentModal = useCallback(() => setRecordPaymentModalVisible((prev) => !prev), []);

  const actionsList = useMemo(() => {
    return [
      {
        title: translate('view', { namespace }),
        faClass: 'fa-eye',
        link: dataItem.viewPath,
        blank: true
      },
      {
        title: translate('download_pdf', { namespace }),
        faClass: 'fa-download',
        link: dataItem.pdfPath,
        blank: true
      },
      {
        title: translate('send_email_here', { namespace }),
        faClass: 'fa-envelope',
        onClick: toggleSendEmailModal
      },
      {
        title: translate('edit', { namespace }),
        faClass: 'fa-edit',
        link: dataItem.editPath,
        disabled: !dataItem.canEdit
      },
      {
        title: translate('add_payment', { namespace }),
        faClass: 'fa-dollar',
        disabled: !(dataItem.canMarkAsPaid && dataItem.status !== 'paid'),
        onClick: toggleRecordPaymentModal
      },
      {
        title: translate('delete', { namespace }),
        faClass: 'fa-trash',
        onClick: toggleDeleteDialog,
        disabled: !dataItem.canDelete
      },
      {
        title: translate('void', { namespace }),
        faClass: 'fa-ban',
        disabled: !dataItem.canVoid,
        onClick: toggleVoidDialog
      }
    ];
  }, [dataItem, toggleDeleteDialog, toggleVoidDialog, toggleSendEmailModal, toggleRecordPaymentModal]);

  const deleteInvoice = useCallback(() => {
    api
      .delete(invoicePath({ id: dataItem.id, format: 'json', _options: true }))
      .then(({ data }) => {
        toast.success(data.message);
        onDataChanged();
      })
      .catch(({ response }) => {
        toast.error(response?.data?.message);
      })
      .finally(() => {
        setDeleteDialogVisible(false);
      });
  }, [dataItem.id, onDataChanged]);

  const voidInvoice = useCallback(() => {
    api
      .post(markAsVoidedInvoicePath({ id: dataItem.id, format: 'json', _options: true }))
      .then(({ data }) => {
        toast.success(data.message);
        onDataChanged();
      })
      .catch(({ response }) => {
        toast.error(response?.data?.message);
      })
      .finally(() => {
        setVoidDialogVisible(false);
      });
  }, [dataItem.id, onDataChanged]);

  return (
    <>
      <KendoActionsCell
        dataItem={dataItem}
        colSpan={colSpan}
        className={className}
        ariaColumnIndex={ariaColumnIndex}
        style={style}
        actionsList={actionsList}
      />

      {deleteDialogVisible && (
        <Dialog
          title={translate('delete_confirmation.title', { namespace })}
          onClose={toggleDeleteDialog}
          width={500}
          className="qmb-dialog">
          <p>{translate('delete_confirmation.message', { namespace })}</p>
          <div className="modal__footer pull-right" style={{ justifyContent: 'space-between' }}>
            <Button variant="secondary" onClick={toggleDeleteDialog}>
              {I18n.t('generic.cancel')}
            </Button>
            <Button variant="primary" onClick={deleteInvoice}>
              {translate('delete', { namespace })}
            </Button>
          </div>
        </Dialog>
      )}

      {voidDialogVisible && (
        <Dialog
          title={translate('void_confirmation.title', { namespace })}
          onClose={toggleVoidDialog}
          width={500}
          className="qmb-dialog">
          <p>{translate('void_confirmation.message', { namespace })}</p>
          <div className="modal__footer pull-right" style={{ justifyContent: 'space-between' }}>
            <Button variant="secondary" onClick={toggleVoidDialog}>
              {I18n.t('generic.cancel')}
            </Button>
            <Button variant="primary" onClick={voidInvoice}>
              {translate('void', { namespace })}
            </Button>
          </div>
        </Dialog>
      )}

      {sendEmailModalVisible && (
        <SendInvoiceModal
          isOpen={sendEmailModalVisible}
          onClose={toggleSendEmailModal}
          invoiceId={dataItem.id}
          customerEmail={dataItem.invoiceModal?.customerEmail}
          subject={dataItem.invoiceModal?.subject}
          body={dataItem.invoiceModal?.body}
          pdfPath={dataItem.pdfPath}
          hasLatestReport={dataItem.invoiceModal?.hasLatestReport}
          defaultAttachPdf={dataItem.invoiceModal?.defaultAttachPdf}
          defaultBccMe={dataItem.invoiceModal?.defaultBccMe}
        />
      )}

      {recordPaymentModalVisible && (
        <RecordPaymentModal onClose={toggleRecordPaymentModal} invoice={dataItem} onDataChanged={onDataChanged} />
      )}
    </>
  );
}

ActionsCell.propTypes = {
  dataItem: PropTypes.object.isRequired,
  colSpan: PropTypes.number.isRequired,
  className: PropTypes.string.isRequired,
  ariaColumnIndex: PropTypes.number.isRequired,
  style: PropTypes.object.isRequired
};

export default ActionsCell;
